"use client";

import React, { useState, useEffect, Suspense } from "react";
import { Shield, FileText, Scale, CheckCircle2 } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "cgu", icon: FileText },
  { id: "privacy", icon: Shield },
  { id: "mentions", icon: Scale },
] as const;

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-4 font-display text-2xl font-extrabold tracking-tight text-espresso">
      {children}
    </h2>
  );
}

function LegalContent() {
  const searchParams = useSearchParams();
  const fr = useLocale() !== "en";
  const T = (a: string, b: string) => (fr ? a : b);
  const locale = fr ? "fr" : "en";
  const [activeTab, setActiveTab] = useState<string>("cgu");

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["cgu", "privacy", "mentions"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const tabLabel = (id: string) =>
    id === "cgu"
      ? T("Conditions d'utilisation", "Terms of use")
      : id === "privacy"
        ? T("Politique de confidentialité", "Privacy policy")
        : T("Mentions légales", "Legal notice");

  const platformLabel = T("Plateforme professionnelle", "Professional platform");
  const appLabel = T("Application mobile Expedys", "Expedys mobile app");

  return (
    <>
      {/* En-tête */}
      <div className="border-b border-line bg-cream-soft py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-7">
          <div className="eyebrow">{T("Centre juridique", "Legal center")}</div>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tightest text-espresso md:text-5xl">
            {T("Centre juridique & ", "Legal center & ")}
            <span className="text-terracotta">{T("contrats", "contracts")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink">
            {T(
              "Retrouvez ici l'ensemble des conditions régissant l'utilisation de la plateforme Expedys. En créant un compte, vous acceptez ces termes qui remplacent tout contrat papier.",
              "Here you will find all the terms governing the use of the Expedys platform. By creating an account, you accept these terms, which replace any paper contract.",
            )}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-7 py-12">
        {/* Onglets */}
        <div className="mb-12 flex flex-wrap gap-2 border-b border-line pb-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 rounded-t-xl px-6 py-3 text-[15px] font-semibold transition-all",
                  active
                    ? "border-b-2 border-terracotta bg-white text-terracotta"
                    : "text-ink-soft hover:bg-cream-soft hover:text-espresso",
                )}
              >
                <Icon className="h-4 w-4" />
                {tabLabel(tab.id)}
              </button>
            );
          })}
        </div>

        <div className="max-w-none pb-24">
          {activeTab === "cgu" && (
            <div className="space-y-10">
              <section className="mb-8 rounded-card-lg border border-[#EBD3C6] bg-[#F4E5DD] p-6">
                <p className="m-0 text-sm font-medium leading-relaxed text-terracotta-dark">
                  <strong>{T("Note importante :", "Important note:")}</strong>{" "}
                  {T(
                    "En créant un compte ou en utilisant les services d'Expedys, le Client accepte sans réserve les présentes conditions. Celles-ci prévalent sur tout échange, accord ou contrat antérieur (incluant les versions PDF ou papier).",
                    "By creating an account or using Expedys services, the Client unreservedly accepts these terms. They prevail over any prior exchange, agreement or contract (including PDF or paper versions).",
                  )}
                </p>
              </section>

              <section>
                <div className="eyebrow mb-3">{platformLabel}</div>
                <H2>{T("1. Objet du Service (SaaS)", "1. Purpose of the Service (SaaS)")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Expedys (par Workless) fournit une licence non-exclusive, révocable et non transférable d'accès à sa plateforme logistique en mode SaaS. Le service inclut : la gestion des colis, l'édition de bordereaux et d'étiquettes, les notifications automatisées (WhatsApp/Email/SMS) et les outils de suivi financier.",
                    "Expedys (by Workless) provides a non-exclusive, revocable and non-transferable license to access its logistics platform in SaaS mode. The service includes: parcel management, waybill and label generation, automated notifications (WhatsApp/Email/SMS) and financial tracking tools.",
                  )}
                </p>
                <p className="mt-2 text-sm italic text-ink-faint">
                  {T(
                    "Note : Sauf accord écrit spécifique, aucune option de développement supplémentaire n'est incluse par défaut dans l'offre standard.",
                    "Note: Unless specifically agreed in writing, no additional development option is included by default in the standard offer.",
                  )}
                </p>
              </section>

              <section className="border-l-4 border-terracotta py-2 pl-6">
                <H2>{T("2. Rôle et Responsabilité (Clause Critique)", "2. Role and Liability (Critical Clause)")}</H2>
                <div className="space-y-4">
                  <div className="flex gap-3 text-ink">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-forest" />
                    <p>
                      <strong>{T("Fournisseur technique :", "Technical provider:")}</strong>{" "}
                      {T(
                        "Workless garantit une disponibilité de la plateforme de 99% (hors maintenances planifiées) et assure la sauvegarde et la sécurité des données.",
                        "Workless guarantees platform availability of 99% (excluding scheduled maintenance) and ensures data backup and security.",
                      )}
                    </p>
                  </div>
                  <div className="flex gap-3 text-ink">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-[#C0392B]" />
                    <p>
                      <strong>{T("Limitation logistique :", "Logistics limitation:")}</strong>{" "}
                      {T("Workless n'est ", "Workless is ")}
                      <strong>
                        {T(
                          "NI transporteur, NI commissionnaire en douane",
                          "NEITHER a carrier NOR a customs broker",
                        )}
                      </strong>
                      .{" "}
                      {T(
                        "Nous ne manipulons aucun colis physiquement. Le Client est seul responsable des marchandises expédiées (pertes, vols, avaries, retards) et de leur conformité légale.",
                        "We do not physically handle any parcel. The Client is solely responsible for the goods shipped (loss, theft, damage, delays) and their legal compliance.",
                      )}
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <H2>{T("3. Abonnement & Paiement", "3. Subscription & Payment")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "L'accès à la plateforme est facturé sous forme d'abonnement (mensuel ou trimestriel) selon le plan choisi ou le devis spécifique accepté par le Client. Les factures sont payables à réception. Tout retard de paiement peut entraîner la suspension automatique du service après un préavis de 48 heures.",
                    "Access to the platform is billed as a subscription (monthly or quarterly) according to the chosen plan or the specific quote accepted by the Client. Invoices are payable on receipt. Any late payment may result in automatic suspension of the service after 48 hours' notice.",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("4. Obligations du Client", "4. Client Obligations")}</H2>
                <ul className="list-disc space-y-3 pl-6 text-ink">
                  <li>
                    <strong>{T("Formation :", "Training:")}</strong>{" "}
                    {T(
                      "Le Client est responsable de la formation de ses agents et du maintien de la confidentialité des identifiants d'accès.",
                      "The Client is responsible for training its agents and keeping access credentials confidential.",
                    )}
                  </li>
                  <li>
                    <strong>{T("Transactions :", "Transactions:")}</strong>{" "}
                    {T(
                      "Le Client assume l'entière responsabilité des transactions financières manuelles (espèces, virements externes) enregistrées dans le système.",
                      "The Client assumes full responsibility for manual financial transactions (cash, external transfers) recorded in the system.",
                    )}
                  </li>
                  <li>
                    <strong>{T("Conformité :", "Compliance:")}</strong>{" "}
                    {T(
                      "Le Client s'engage à respecter les lois douanières et de transport de chaque pays d'opération (Guinée, Canada, etc.).",
                      "The Client undertakes to comply with the customs and transport laws of each country of operation (Guinea, Canada, etc.).",
                    )}
                  </li>
                </ul>
              </section>

              <section>
                <H2>{T("5. Résiliation & Récupération des données", "5. Termination & Data Recovery")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Le contrat est conclu pour une durée indéterminée. Chaque partie peut résilier avec un préavis de 30 jours. En cas de résiliation, Workless s'engage à laisser un accès en lecture seule pendant 30 jours pour permettre au Client de récupérer ses données (formats CSV/Excel) avant leur suppression définitive et sécurisée.",
                    "The contract is concluded for an indefinite period. Either party may terminate with 30 days' notice. Upon termination, Workless undertakes to provide read-only access for 30 days to allow the Client to retrieve its data (CSV/Excel formats) before its final and secure deletion.",
                  )}
                </p>
              </section>

              {/* ── Volet application mobile (particuliers) ── */}
              <section className="border-t border-line pt-10">
                <div className="eyebrow mb-3">{appLabel}</div>
                <H2>{T("6. Application mobile (particuliers)", "6. Mobile app (individuals)")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "L'application mobile Expedys permet aux particuliers (notamment la diaspora) d'expédier des colis vers la Guinée et l'Afrique. Son utilisation est gratuite et sans abonnement. La création d'un compte vaut acceptation des présentes conditions.",
                    "The Expedys mobile app lets individuals (notably the diaspora) ship parcels to Guinea and Africa. It is free to use and requires no subscription. Creating an account constitutes acceptance of these terms.",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("7. Compte & contenu des colis", "7. Account & parcel contents")}</H2>
                <ul className="list-disc space-y-3 pl-6 text-ink">
                  <li>
                    <strong>{T("Exactitude :", "Accuracy:")}</strong>{" "}
                    {T(
                      "l'utilisateur est responsable de l'exactitude des informations fournies (expéditeur, destinataires, contenu et valeur déclarée du colis).",
                      "the user is responsible for the accuracy of the information provided (sender, recipients, contents and declared value of the parcel).",
                    )}
                  </li>
                  <li>
                    <strong>{T("Licéité :", "Lawfulness:")}</strong>{" "}
                    {T(
                      "l'utilisateur garantit que le contenu de chaque colis est licite et conforme aux règles douanières et aux interdictions de transport. Tout colis non conforme peut être refusé, retenu ou inspecté.",
                      "the user warrants that the contents of each parcel are lawful and compliant with customs rules and transport prohibitions. Any non-compliant parcel may be refused, withheld or inspected.",
                    )}
                  </li>
                  <li>
                    <strong>{T("Interdits :", "Prohibited items:")}</strong>{" "}
                    {T(
                      "sont notamment prohibés les marchandises dangereuses, illégales ou non déclarées.",
                      "dangerous, illegal or undeclared goods are notably prohibited.",
                    )}
                  </li>
                </ul>
              </section>

              <section>
                <H2>{T("8. Prix, paiement & livraison", "8. Price, payment & delivery")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Le prix « tout compris » (transport, douane, remise) est affiché dans l'application avant validation. Le paiement s'effectue de façon sécurisée via nos prestataires (Apple Pay, carte bancaire, Wallet Expedys, Orange Money, MTN MoMo…). Les délais de livraison (aérien ou maritime) sont indicatifs et ne constituent pas une garantie de délai.",
                    "The “all-in” price (transport, customs, delivery) is shown in the app before confirmation. Payment is made securely via our providers (Apple Pay, bank card, Expedys Wallet, Orange Money, MTN MoMo…). Delivery times (air or ocean) are indicative and do not constitute a delivery guarantee.",
                  )}
                </p>
              </section>

              <section className="border-l-4 border-terracotta py-2 pl-6">
                <H2>{T("9. Rôle d'Expedys (application)", "9. Expedys's role (app)")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Expedys (par Workless) édite l'application et organise l'acheminement des colis via son réseau d'agences et de transporteurs partenaires. Notre responsabilité s'exerce dans la limite des prestations effectivement fournies ; l'utilisateur demeure responsable de la conformité légale du contenu expédié.",
                    "Expedys (by Workless) publishes the app and organizes parcel routing through its network of partner agencies and carriers. Our liability applies within the limit of the services actually provided; the user remains responsible for the legal compliance of the contents shipped.",
                  )}
                </p>
              </section>
            </div>
          )}

          {activeTab === "privacy" && (
            <div className="space-y-10">
              <section className="mb-2 rounded-card-lg border border-line bg-cream-soft p-6">
                <p className="m-0 text-sm font-medium leading-relaxed text-ink">
                  {T(
                    "Cette politique couvre à la fois la ",
                    "This policy covers both the ",
                  )}
                  <strong>{platformLabel}</strong>
                  {T(" (agences) et l'", " (agencies) and the ")}
                  <strong>{appLabel}</strong>
                  {T(" (particuliers).", " (individuals).")}
                </p>
              </section>

              <section>
                <div className="eyebrow mb-3">{platformLabel}</div>
                <H2>{T("1. Données Collectées", "1. Data Collected")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Pour le bon fonctionnement du service logistique, nous traitons les données suivantes : coordonnées des agents du client (Nom, Email, Tel) et informations relatives aux expéditeurs et destinataires (Noms, Téléphones, Adresses de livraison).",
                    "For the proper functioning of the logistics service, we process the following data: contact details of the client's agents (Name, Email, Phone) and information about senders and recipients (Names, Phones, Delivery addresses).",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("2. Usage et Sécurité", "2. Use and Security")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Toutes les données traitées sont strictement confidentielles. Elles ne sont utilisées que pour l'exécution du service (génération de bordereaux, notifications de suivi, statistiques). Nous utilisons des protocoles de chiffrement et des serveurs sécurisés conformes aux normes canadiennes pour protéger ces informations.",
                    "All processed data is strictly confidential. It is used only to perform the service (waybill generation, tracking notifications, statistics). We use encryption protocols and secure servers compliant with Canadian standards to protect this information.",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("3. Conservation des données", "3. Data Retention")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Les données sont conservées pendant toute la durée de vie du contrat. En cas de résiliation, elles sont archivées pendant une période de 30 jours pour permettre leur extraction, puis sont supprimées de nos bases de production, sauf obligation légale de conservation comptable ou fiscale.",
                    "Data is kept for the entire duration of the contract. Upon termination, it is archived for 30 days to allow extraction, then deleted from our production databases, except where legally required to keep accounting or tax records.",
                  )}
                </p>
              </section>

              {/* ── Volet application mobile (particuliers) ── */}
              <section className="border-t border-line pt-10">
                <div className="eyebrow mb-3">{appLabel}</div>
                <H2>{T("4. Données des utilisateurs de l'application", "4. App users' data")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Lors de l'utilisation de l'application mobile, nous traitons : votre identité (nom, numéro de téléphone, e-mail), les coordonnées de vos destinataires (nom, téléphone, adresse de livraison), les détails de vos colis, ainsi que des données techniques (type d'appareil, identifiants de notification). Les données de paiement sont traitées directement par nos prestataires sécurisés (Apple Pay, Stripe, Orange Money, MTN MoMo…) et ne sont pas conservées par Expedys.",
                    "When using the mobile app, we process: your identity (name, phone number, email), your recipients' details (name, phone, delivery address), your parcel details, and technical data (device type, notification identifiers). Payment data is processed directly by our secure providers (Apple Pay, Stripe, Orange Money, MTN MoMo…) and is not stored by Expedys.",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("5. Usage & partage", "5. Use & sharing")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "Ces données servent uniquement à l'exécution de vos envois : préparation et suivi des colis, notifications (WhatsApp, SMS, e-mail), paiement et support. Elles peuvent être partagées avec nos agences et transporteurs partenaires pour l'acheminement, avec les prestataires de paiement, et avec les autorités douanières lorsque la loi l'exige.",
                    "This data is used solely to fulfill your shipments: parcel preparation and tracking, notifications (WhatsApp, SMS, email), payment and support. It may be shared with our partner agencies and carriers for routing, with payment providers, and with customs authorities where required by law.",
                  )}
                </p>
              </section>

              <section>
                <H2>{T("6. Permissions, droits & suppression", "6. Permissions, rights & deletion")}</H2>
                <p className="leading-relaxed text-ink">
                  {T(
                    "L'application peut demander certaines autorisations (notifications, et selon les fonctionnalités, appareil photo pour scanner). Vous pouvez à tout moment accéder à vos données, les rectifier ou supprimer votre compte. La suppression depuis l'application est immédiate : nom, téléphone, courriel, photo de profil, adresse et liens avec un compte Apple ou Google sont effacés sur-le-champ. Si vous avez désinstallé l'application, écrivez à contact@expedys.com : nous traitons la demande sous 30 jours après vérification de votre identité. L'historique de vos envois et paiements est conservé pour nos obligations comptables et de lutte contre le blanchiment, rattaché à un compte vidé de toute donnée personnelle.",
                    "The app may request certain permissions (notifications, and depending on features, camera to scan). You may at any time access your data, correct it or delete your account. Deletion from the app is immediate: name, phone number, email, profile photo, address and links to an Apple or Google account are erased at once. If you have uninstalled the app, write to contact@expedys.com: we process the request within 30 days, after verifying your identity. The history of your shipments and payments is kept for our accounting and anti-money-laundering obligations, attached to an account stripped of all personal data.",
                  )}
                </p>
                <p className="mt-4 leading-relaxed text-ink">
                  <Link
                    href={`/${locale}/suppression-compte`}
                    className="font-semibold text-terracotta underline underline-offset-4"
                  >
                    {T(
                      "Marche à suivre détaillée pour supprimer votre compte",
                      "Detailed steps to delete your account",
                    )}
                  </Link>
                </p>
              </section>
            </div>
          )}

          {activeTab === "mentions" && (
            <div className="space-y-10">
              <section>
                <H2>{T("1. Éditeur", "1. Publisher")}</H2>
                <ul className="list-none space-y-2 p-0 leading-relaxed text-ink">
                  <li>
                    <strong>{T("Siège Social :", "Registered office:")}</strong>{" "}
                    {T("Montréal, Québec, Canada.", "Montreal, Quebec, Canada.")}
                  </li>
                  <li>
                    <strong>{T("Succursale Opérationnelle :", "Operational branch:")}</strong>{" "}
                    {T(
                      "Conakry, République de Guinée.",
                      "Conakry, Republic of Guinea.",
                    )}
                  </li>
                  <li>
                    <strong>{T("Contact :", "Contact:")}</strong>{" "}
                    {T(
                      "contact@expedys.com | +1 438 544 0767 (Canada) | +224 624 830 921 (Guinée)",
                      "contact@expedys.com | +1 438 544 0767 (Canada) | +224 624 830 921 (Guinea)",
                    )}
                  </li>
                </ul>
              </section>

              <section className="rounded-card-lg border border-line bg-cream-soft p-8">
                <H2>{T("2. Droit Applicable & Juridiction", "2. Governing Law & Jurisdiction")}</H2>
                <div className="space-y-6 text-ink">
                  <div>
                    <h3 className="mb-2 font-display text-lg font-bold text-espresso">
                      {T("Volet Numérique (Logiciel)", "Digital scope (Software)")}
                    </h3>
                    <p>
                      {T(
                        "Tout litige relatif à l'utilisation du logiciel, à sa licence ou à la propriété intellectuelle est soumis aux lois de la Province de ",
                        "Any dispute relating to the use of the software, its license or intellectual property is subject to the laws of the Province of ",
                      )}
                      <strong>{T("Québec, Canada", "Quebec, Canada")}</strong>.
                    </p>
                  </div>
                  <div>
                    <h3 className="mb-2 font-display text-lg font-bold text-espresso">
                      {T("Volet Opérationnel (Transport)", "Operational scope (Transport)")}
                    </h3>
                    <p>
                      {T(
                        "Tout litige relatif aux opérations physiques, à la manutention ou aux transactions locales sur le sol guinéen est soumis aux tribunaux compétents de la ",
                        "Any dispute relating to physical operations, handling or local transactions on Guinean soil is subject to the competent courts of the ",
                      )}
                      <strong>{T("République de Guinée", "Republic of Guinea")}</strong>.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function LegalClient() {
  return (
    <main className="min-h-screen bg-white">
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center text-ink-faint">
            …
          </div>
        }
      >
        <LegalContent />
      </Suspense>
    </main>
  );
}
